import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plane, Clock, FileCheck, Tag, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getUserRequests } from '../services/requests';

interface RequestHistoryProps {
  onBack: () => void;
}

interface Request {
  id: string;
  type: string;
  status: string;
  data: any;
  created_at: string;
  completed_at?: string;
  admin_notes?: string;
  admin?: {
    name: string;
    email: string;
  };
}

export default function RequestHistory({ onBack }: RequestHistoryProps) {
  const { user } = useAuth();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchRequests();
    }
  }, [user]);

  const fetchRequests = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { requests: userRequests, error: requestError } = await getUserRequests(user.id);
      
      if (requestError) throw new Error(requestError);
      if (userRequests) {
        setRequests(userRequests);
      }
    } catch (err) {
      setError('Failed to load requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRequestIcon = (type: string) => {
    switch (type) {
      case 'flight_quote':
        return <Plane size={20} className="text-blue-500" />;
      case 'booking':
        return <Clock size={20} className="text-green-500" />;
      case 'document':
        return <FileCheck size={20} className="text-purple-500" />;
      case 'payment':
        return <Tag size={20} className="text-orange-500" />;
      default:
        return <AlertTriangle size={20} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-start justify-end">
      <div className="w-full max-w-md h-screen bg-white border-l border-gray-100 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-bold">Request History</h2>
              <p className="text-sm text-gray-500">View your requests and their status</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Error Loading Requests</h3>
              <p className="text-gray-500 mt-2">{error}</p>
              <button 
                onClick={fetchRequests}
                className="mt-4 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : requests.length > 0 ? (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                          {getRequestIcon(request.type)}
                        </div>
                        <div>
                          <div className="font-medium capitalize">
                            {request.type.replace('_', ' ')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(request.created_at)}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </div>

                    {/* Request Details */}
                    <div className="text-sm text-gray-600 space-y-2">
                      {request.data && (
                        <pre className="whitespace-pre-wrap font-sans">
                          {JSON.stringify(request.data, null, 2)}
                        </pre>
                      )}
                      
                      {request.admin_notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <div className="font-medium text-gray-700">Admin Notes:</div>
                          <p className="mt-1">{request.admin_notes}</p>
                        </div>
                      )}

                      {request.completed_at && (
                        <div className="mt-2 text-xs text-gray-500">
                          Completed: {formatDate(request.completed_at)}
                        </div>
                      )}

                      {request.admin && (
                        <div className="mt-2 text-xs text-gray-500">
                          Handled by: {request.admin.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={24} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No Requests Yet</h3>
              <p className="text-gray-500 mt-2">
                Your requests will appear here once you make your first booking or support request.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}